import { apiClient } from "./client";
import type {
  VariableGroup,
  VariableGroupCreate,
  VariableGroupUpdate,
  VariableGroupsPaginated,
  VariableGroupWithVariables,
  Variable,
  VariableCreate,
  VariableUpdate,
  VariablesPaginated,
  MessageResponse,
} from "@/types";

// Variable Groups
interface ListGroupsParams {
  page?: number;
  page_size?: number;
}

export async function listVariableGroups(
  versionId: number,
  params: ListGroupsParams = {}
): Promise<VariableGroupsPaginated> {
  const response = await apiClient.get<VariableGroupsPaginated>(
    `/simulators/versions/${versionId}/variable-groups`,
    { params }
  );
  return response.data;
}

export async function listAllVariableGroups(
  versionId: number
): Promise<VariableGroupWithVariables[]> {
  const response = await apiClient.get<VariableGroupWithVariables[]>(
    `/simulators/versions/${versionId}/variable-groups/all`
  );
  return response.data;
}

export async function getVariableGroup(
  versionId: number,
  groupId: number
): Promise<VariableGroupWithVariables> {
  const response = await apiClient.get<VariableGroupWithVariables>(
    `/simulators/versions/${versionId}/variable-groups/${groupId}`
  );
  return response.data;
}

export async function createVariableGroup(
  versionId: number,
  data: VariableGroupCreate
): Promise<VariableGroup> {
  const response = await apiClient.post<VariableGroup>(
    `/simulators/versions/${versionId}/variable-groups`,
    data
  );
  return response.data;
}

export async function updateVariableGroup(
  versionId: number,
  groupId: number,
  data: VariableGroupUpdate
): Promise<VariableGroup> {
  const response = await apiClient.patch<VariableGroup>(
    `/simulators/versions/${versionId}/variable-groups/${groupId}`,
    data
  );
  return response.data;
}

export async function deleteVariableGroup(
  versionId: number,
  groupId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/variable-groups/${groupId}`
  );
  return response.data;
}

// Variables
interface ListVariablesParams {
  page?: number;
  page_size?: number;
  group_id?: number;
}

export async function listVariables(
  versionId: number,
  params: ListVariablesParams = {}
): Promise<VariablesPaginated> {
  const response = await apiClient.get<VariablesPaginated>(
    `/simulators/versions/${versionId}/variables`,
    { params }
  );
  return response.data;
}

export async function getVariable(
  versionId: number,
  variableId: number
): Promise<Variable> {
  const response = await apiClient.get<Variable>(
    `/simulators/versions/${versionId}/variables/${variableId}`
  );
  return response.data;
}

export async function createVariable(
  versionId: number,
  data: VariableCreate
): Promise<Variable> {
  const response = await apiClient.post<Variable>(
    `/simulators/versions/${versionId}/variables`,
    data
  );
  return response.data;
}

export async function updateVariable(
  versionId: number,
  variableId: number,
  data: VariableUpdate
): Promise<Variable> {
  const response = await apiClient.patch<Variable>(
    `/simulators/versions/${versionId}/variables/${variableId}`,
    data
  );
  return response.data;
}

export async function deleteVariable(
  versionId: number,
  variableId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/variables/${variableId}`
  );
  return response.data;
}

// Reordering
export async function reorderVariableGroups(
  versionId: number,
  groupOrders: { id: number; order: number }[]
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/versions/${versionId}/variable-groups/reorder`,
    { group_orders: groupOrders }
  );
  return response.data;
}

export async function reorderVariables(
  versionId: number,
  variableOrders: { id: number; order: number }[]
): Promise<MessageResponse> {
  const payload = { variable_orders: variableOrders };
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/versions/${versionId}/variables/reorder`,
    payload
  );
  return response.data;
}

